import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Button, message, Grid, Drawer } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  BellOutlined,
  UserOutlined
} from '@ant-design/icons';
import api from './utils/api';
import Sidebar from './components/Sidebar';
import LoginForm from './components/LoginForm';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Settlements from './pages/Settlements';
import Payments from './pages/Payments';
import PaymentMethods from './pages/PaymentMethods';

import Users from './pages/Users';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Security from './pages/Security';
import Config from './pages/Config';
import CallbackConfig from './pages/CallbackConfig';
import MasterProducts from './pages/MasterProducts';
import FeeTiers from './pages/FeeTiers';
import CashInOut from './pages/CashInOut';
import Members from './pages/Members';
import MerchantFinance from './pages/merchant/MerchantFinance';
import CashIn from './pages/CashIn';
import QRISMerchants from './pages/QRISMerchants';
import QRISPayment from './pages/QRISPayment';
import APIDocumentation from './pages/APIDocumentation';
import MerchantApproval from './pages/MerchantApproval';
import TPSTesting from './pages/TPSTestingFinal';
import SupplierManagement from './pages/SupplierManagement';
import CategoryManagement from './pages/CategoryManagement';
import DepositValidation from './pages/admin/DepositValidation';
import SupplierReconciliation from './pages/admin/SupplierReconciliation';
import DepositBanks from './pages/admin/DepositBanks';
import AdminMerchantBankAccounts from './pages/admin/MerchantBankAccounts';
import WithdrawalApproval from './pages/admin/WithdrawalApproval';
import WithdrawalSettings from './pages/admin/WithdrawalSettings';
import AdminMerchantFinance from './pages/admin/MerchantFinance';
import ProductRouting from './pages/ProductRouting';
import Profile from './pages/Profile';
import ProcessingTransactions from './pages/ProcessingTransactions';

const { Content, Header, Sider } = Layout;

function App() {
  console.log('🚀 App component is loading...');

  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg; // < 992px dianggap mobile/tablet
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, false = not auth, true = authenticated
  const [user, setUser] = useState(null);

  // Tutup drawer mobile setiap pindah halaman
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  console.log('🔐 Authentication state:', isAuthenticated);
  console.log('👤 User state:', user);

  // Check authentication on mount
  useEffect(() => {
    console.log('🔄 Checking authentication...');
    let token = localStorage.getItem('access_token');
    let userData = localStorage.getItem('user');

    console.log('🎫 Token found:', !!token);
    console.log('👤 User data found:', !!userData);

    const isTokenExpired = (t) => {
      try {
        const payload = JSON.parse(atob(t.split('.')[1]));
        return payload.exp * 1000 < Date.now();
      } catch {
        return true;
      }
    };

    if (token && userData && !isTokenExpired(token)) {
      console.log('✅ User authenticated');
      setUser(JSON.parse(userData));
      setIsAuthenticated(true);
    } else {
      console.log('❌ User not authenticated or token expired');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData.user);
    setIsAuthenticated(true);

    // Token will be handled by api interceptor automatically
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);

    // Token will be automatically removed by api interceptor

    message.success('Logged out successfully');
  };

  const handleMenuClick = (key) => {
    switch (key) {
      case 'profile':
        navigate('/admin/profile');
        break;
      case 'settings':
        navigate('/admin/settings');
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    console.log('⏳ Showing loading screen...');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f0f2f5'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // If not authenticated, show login form
  if (isAuthenticated === false) {
    console.log('🔐 Showing login form...');
    return <LoginForm onLogin={handleLogin} />;
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
    },
    {
      key: 'settings',
      icon: <UserOutlined />,
      label: 'Pengaturan',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Keluar',
      danger: true,
    },
  ];

  console.log('📍 Current URL path:', window.location.pathname);
  console.log('🎯 Rendering main layout...');

  // Konten sidebar (logo + menu); clp = collapsed
  const sidebarInner = (clp) => (
    <>
      <div className="flex items-center justify-center h-20 text-white fade-in">
        {clp ? (
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-xl font-bold shadow-lg">
            PG
          </div>
        ) : (
          <div className="text-center">
            <div className="text-lg font-bold mb-1">{process.env.REACT_APP_NAME || 'Arendo'}</div>
            <div className="text-xs opacity-75">{process.env.REACT_APP_DESCRIPTION || 'Dashboard System'}</div>
          </div>
        )}
      </div>
      <Sidebar collapsed={clp} />
    </>
  );

  const sidebarBg = 'linear-gradient(180deg, #1e293b 0%, #334155 100%)';

  return (
    <Layout style={{ minHeight: '100vh' }}>
        {isMobile ? (
          <Drawer
            placement="left"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            width={260}
            closable={false}
            styles={{ body: { padding: 0, background: sidebarBg }, header: { display: 'none' } }}
          >
            {sidebarInner(false)}
          </Drawer>
        ) : (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={280}
            collapsedWidth={80}
            style={{
              overflow: 'auto',
              height: '100vh',
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              background: sidebarBg,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              zIndex: 1001,
            }}
          >
            {sidebarInner(collapsed)}
          </Sider>
        )}

        <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 280) }}>
          <Header
            style={{
              padding: isMobile ? '0 16px' : '0 32px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              position: 'fixed',
              top: 0,
              right: 0,
              left: isMobile ? 0 : (collapsed ? 80 : 280),
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div className="flex items-center space-x-4">
              <Button
                type="text"
                icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
                onClick={() => isMobile ? setMobileOpen(true) : setCollapsed(!collapsed)}
                className="modern-btn"
                style={{
                  background: 'transparent',
                  color: 'var(--dark)',
                  border: '1px solid var(--gray-light)',
                }}
              />
            </div>

            <div className="flex items-center space-x-6">
              <div className="relative">
                <BellOutlined
                  style={{
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: 'var(--gray)',
                    transition: 'all 0.3s ease'
                  }}
                  className="hover:color-var(--primary)"
                />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full pulse"></div>
              </div>
              <Dropdown
                menu={{
                  items: userMenuItems.map(item => ({
                    ...item,
                    onClick: () => handleMenuClick(item.key)
                  }))
                }}
                placement="bottomRight"
              >
                <div className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 px-4 py-2 rounded-lg transition-all duration-300">
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  {!isMobile && (
                    <div>
                      <div className="font-semibold text-sm" style={{ color: 'var(--dark)' }}>
                        {user?.full_name || user?.username || 'User'}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--gray)' }}>
                        {user?.role?.replace('_', ' ')?.toUpperCase() || 'USER'}
                      </div>
                    </div>
                  )}
                </div>
              </Dropdown>
            </div>
          </Header>

          <Content style={{
            margin: isMobile ? '12px' : '32px',
            padding: isMobile ? '14px' : '32px',
            marginTop: isMobile ? 76 : 88,
            minHeight: 'calc(100vh - 88px)',
            background: 'transparent'
          }}>
            <Routes>
              <Route path="/" element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="processing-transactions" element={<ProcessingTransactions />} />
              <Route path="cash-inout" element={<CashInOut />} />
              <Route path="settlements" element={<Settlements />} />
              <Route path="payments" element={<Payments />} />
              <Route path="payment-methods" element={<PaymentMethods />} />
              <Route path="users" element={<Users />} />
              <Route path="members" element={<Members />} />
              <Route path="deposit-validation" element={<DepositValidation />} />
              <Route path="supplier-reconciliation" element={<SupplierReconciliation />} />
              <Route path="deposit-banks" element={<DepositBanks />} />
              <Route path="merchant-bank-accounts" element={<AdminMerchantBankAccounts />} />
              <Route path="withdrawal-approval" element={<WithdrawalApproval />} />
              <Route path="withdrawal-settings" element={<WithdrawalSettings />} />
              <Route path="test-route" element={
                <div style={{ padding: '24px', backgroundColor: 'red', color: 'white' }}>
                  <h1>TEST ROUTE WORKS!</h1>
                  <p>This is a test to verify routing is working.</p>
                </div>
              } />
              <Route path="merchant-approval" element={<MerchantApproval />} />
              <Route path="qris-merchants" element={<QRISMerchants />} />
              <Route path="qris-payment" element={<QRISPayment />} />
              <Route path="tps-testing" element={<TPSTesting />} />
              <Route path="supplier-management" element={<SupplierManagement />} />
              <Route path="category-management" element={<CategoryManagement />} />
              <Route path="reports" element={<Reports />} />
              <Route path="security" element={<Security />} />
              <Route path="callback-config" element={<CallbackConfig />} />
              <Route path="settings" element={<Settings />} />
              <Route path="config" element={<Config />} />
              <Route path="master-products" element={<MasterProducts />} />
              <Route path="product-routing" element={<ProductRouting />} />
              <Route path="fee-tiers" element={<FeeTiers />} />
              <Route path="merchant-finance" element={<MerchantFinance />} />
              <Route path="merchant-balances" element={<AdminMerchantFinance />} />
              <Route path="cash-in" element={<CashIn />} />
            </Routes>
          </Content>
        </Layout>
      </Layout>
  );
}

export default App;